import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Chip,
  LinearProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Paper,
  Alert,
} from '@mui/material';
import {
  Logout,
  Assignment,
  ShoppingCart,
  EmojiEvents,
  TrendingUp,
  CheckCircle,
  Add,
  AttachMoney,
  LocalGroceryStore,
  CalendarToday,
  PhotoLibrary,
  Chat,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TaskCard from '../components/TaskCard';
import ProfileCustomization from '../components/ProfileCustomization';
import CompletedTasksCard from '../components/CompletedTasksCard';
import FeedbackWidget from '../components/FeedbackWidget';

// Age-gated AI: 13+ gets EarnEChat (real AI), under-13 gets PreProgrammedBot (COPPA-compliant)
import EarnEChat from '../components/EarnEChat';
import PreProgrammedBot from '../components/PreProgrammedBot';
import RewardsStore from '../components/RewardsStore';
import { calculateAge } from '../utils/childAuthHelper';
import SavingsGoalsEnhanced from '../components/SavingsGoalsEnhanced';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import { useFamilyId } from '../contexts/FamilyIdContext';
import EmergencyOverlay from '../components/EmergencyOverlay';
import { useScreenTimeRules } from '../hooks/useScreenTimeRules';

function ChildDashboard() {
  const { currentUser, userProfile, logout } = useAuth();
  const { tasks, shoppingList, addShoppingItem } = useFamily();
  const { familyData, familyId } = useFamilyId();

  // Screen time lock state
  const { isCurrentlyLocked, activeRules, nextUnlockTime } = useScreenTimeRules(familyId, currentUser?.uid);
  const navigate = useNavigate();
  const [openShoppingDialog, setOpenShoppingDialog] = useState(false);
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [openAllowanceDialog, setOpenAllowanceDialog] = useState(false);
  const [openTasksDialog, setOpenTasksDialog] = useState(false);
  const [openCompletedDialog, setOpenCompletedDialog] = useState(false);
  const [showRewards, setShowRewards] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    price: 0,
    category: ''
  });

  // Force scroll to top on mount to prevent auto-scroll from child components
  useEffect(() => {
    // Immediate scroll
    window.scrollTo(0, 0);
    
    // Multiple delayed scrolls to override any child component scrolls (especially EarnEChat)
    const timer1 = setTimeout(() => window.scrollTo(0, 0), 50);
    const timer2 = setTimeout(() => window.scrollTo(0, 0), 100);
    const timer3 = setTimeout(() => window.scrollTo(0, 0), 200);
    const timer4 = setTimeout(() => window.scrollTo(0, 0), 500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleAddShoppingItem = async () => {
    try {
      await addShoppingItem({
        ...newItem,
        requestedBy: userProfile?.displayName,
        requestedByUid: currentUser.uid
      });
      setOpenShoppingDialog(false);
      setNewItem({ name: '', price: 0, category: '' });
    } catch (error) {
      console.error('Error adding shopping item:', error);
    }
  };

  const myTasks = tasks.filter(task => 
    task.assignedTo?.includes(currentUser.uid)
  );
  const pendingTasks = myTasks.filter(task => task.status === 'pending');
  const completedTasks = myTasks.filter(task => task.status === 'completed');

  // Age-gating: use dateOfBirth from userProfile.
  // If missing or age < 13 → PreProgrammedBot (safe COPPA default).
  // If age >= 13 → real EarnEChat AI (with parental consent implied by profile).
  const childAge = calculateAge(userProfile?.dateOfBirth);
  const showRealAI = childAge !== null && childAge >= 13;
  const myShoppingRequests = shoppingList.filter(item => 
    item.requestedByUid === currentUser.uid
  );

  const totalEarned = completedTasks.reduce((sum, task) => sum + (task.allowance || 0), 0);
  const currentAllowance = userProfile?.allowance || 0;

  // Configurable allowance goal (TODO: Wire to Firebase config/user settings)
  const allowanceGoal = 50; // Default value, can be updated via Firebase later
  const progressPercentage = Math.min((currentAllowance / allowanceGoal) * 100, 100);

  // Get user's custom theme and background
  const userTheme = userProfile?.theme;
  const userBackground = userProfile?.backgroundPattern;

  return (
    <Box 
      sx={{ 
        flexGrow: 1, 
        minHeight: '100vh', 
        bgcolor: userBackground?.gradient ? '#f5f5f5' : 'background.default',
        backgroundImage: userBackground?.gradient || 'none',
        backgroundSize: userBackground?.size || 'auto',
        backgroundPosition: userBackground?.position || '0 0',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* App Bar */}
      <AppBar 
        position="static" 
        sx={{ 
          bgcolor: userTheme?.primary || 'primary.main',
          background: userTheme ? `linear-gradient(45deg, ${userTheme.primary}, ${userTheme.secondary})` : 'primary.main'
        }}
      >
        <Toolbar>
          <Box sx={{ mr: 1, fontSize: 28 }}>🤖</Box>
          <Typography variant="h6" component="div" noWrap sx={{ flexGrow: 1, fontFamily: "'Poppins', sans-serif", overflow: 'hidden', fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
            Earn-E's Corner
          </Typography>
          <IconButton 
            color="inherit" 
            sx={{ display: { xs: 'none', sm: 'flex' } }}
            onClick={() => {
              console.log('📅 Navigating to calendar from child dashboard');
              navigate('/calendar');
            }}
          >
            <CalendarToday />
          </IconButton>
          <IconButton 
            color="inherit" 
            sx={{ display: { xs: 'none', sm: 'flex' } }}
            onClick={() => {
              console.log('🛒 Navigating to shopping from child dashboard');
              navigate('/shopping');
            }}
          >
            <ShoppingCart />
          </IconButton>
          <IconButton 
            color="inherit" 
            sx={{ display: { xs: 'none', sm: 'flex' } }}
            onClick={() => {
              console.log('🖼️ Navigating to gallery from child dashboard');
              navigate('/gallery');
            }}
          >
            <PhotoLibrary />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={() => navigate('/messages')}
            title="Message Parents"
          >
            <Chat />
          </IconButton>
          <IconButton onClick={() => setOpenProfileDialog(true)}>
            <Avatar 
              src={userProfile?.photoURL}
              sx={{ bgcolor: 'secondary.main', cursor: 'pointer' }}
            >
              {!userProfile?.photoURL && userProfile?.displayName?.charAt(0)}
            </Avatar>
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Screen Time Lock Banner */}
        {isCurrentlyLocked && (
          <Alert
            severity="error"
            icon={false}
            sx={{
              mb: 3,
              background: 'linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)',
              color: 'white',
              '& .MuiAlert-message': { width: '100%' },
              borderRadius: 2,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'white' }}>
                  🔒 Screen Time Active
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  {activeRules && activeRules[0]?.type === 'chore'
                    ? 'Complete your chore to unlock'
                    : nextUnlockTime
                      ? `Unlocks at ${new Date(nextUnlockTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                      : 'Device is locked to Earn-E'}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Tap 🚨 for emergency contacts
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Welcome Section */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.main' }}>
            Hey {userProfile?.displayName}! 🌟
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Ready to tackle today's challenges?
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%', 
                bgcolor: 'primary.light', 
                color: 'white',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: 6
                }
              }}
              onClick={() => setOpenAllowanceDialog(true)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      My Allowance
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      ${currentAllowance.toFixed(2)}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 40 }} />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    mt: 2,
                    bgcolor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'white'
                    }
                  }}
                />
                <Typography variant="caption" sx={{ opacity: 0.9, mt: 1, display: 'block' }}>
                  Goal: ${allowanceGoal} ({progressPercentage.toFixed(0)}%)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%', 
                bgcolor: 'warning.light', 
                color: 'white',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: 6
                }
              }}
              onClick={() => setOpenTasksDialog(true)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Tasks to Do
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {pendingTasks.length}
                    </Typography>
                  </Box>
                  <Assignment sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%', 
                bgcolor: 'success.light', 
                color: 'white',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: 6
                }
              }}
              onClick={() => setOpenCompletedDialog(true)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Completed
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {completedTasks.length}
                    </Typography>
                  </Box>
                  <CheckCircle sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%', 
                bgcolor: 'secondary.light', 
                color: 'white',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: 6
                }
              }}
              onClick={() => setOpenAllowanceDialog(true)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Earned
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      ${totalEarned.toFixed(2)}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Navigation (mobile-first) — fixes N-001: nav icons hidden on mobile */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1, mb: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarToday />}
            onClick={() => navigate('/calendar')}
            sx={{ minHeight: 48, minWidth: 100 }}
          >
            Calendar
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ShoppingCart />}
            onClick={() => navigate('/shopping')}
            sx={{ minHeight: 48, minWidth: 100 }}
          >
            Shopping
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoLibrary />}
            onClick={() => navigate('/gallery')}
            sx={{ minHeight: 48, minWidth: 100 }}
          >
            Gallery
          </Button>
        </Box>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* AI Assistant — age-gated per COPPA */}
          {/* Under-13 (or no DOB on file) → Chip the pre-programmed bot (no data stored, no API calls) */}
          {/* 13+ → EarnEChat real AI */}
          <Grid item xs={12}>
            {showRealAI ? (
              <EarnEChat
                tasks={myTasks}
                completedTasks={completedTasks}
                userProfile={userProfile}
                familyMembers={[]}
                currentUser={currentUser}
              />
            ) : (
              <PreProgrammedBot
                childName={userProfile?.displayName || userProfile?.firstName}
                tasks={pendingTasks}
                completedTasks={completedTasks}
              />
            )}
          </Grid>

          {/* My Tasks */}
          <Grid item xs={12} md={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment color="primary" />
                My Tasks
              </Typography>
            </Box>
            {myTasks.length === 0 ? (
              <Card>
                <CardContent>
                  <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <EmojiEvents sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No tasks assigned yet!
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check back later or ask your parents for new tasks.
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={2}>
                {myTasks.map((task) => (
                  <Grid item xs={12} key={task.id}>
                    <TaskCard task={task} isParent={false} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          {/* Shopping Requests */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingCart color="primary" />
                  My Shopping Requests
                </Typography>
                <List>
                  {myShoppingRequests.slice(0, 5).map((item) => (
                    <ListItem key={item.id} divider>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          <LocalGroceryStore />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.name}
                        secondary={`$${item.price || 0}`}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={item.status}
                          color={item.status === 'approved' ? 'success' : 'warning'}
                          size="small"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                  {myShoppingRequests.length === 0 && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No requests yet
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Savings Goals */}
          <Grid item xs={12}>
            <SavingsGoalsEnhanced 
              userId={currentUser.uid}
              currentBalance={userProfile?.allowance || 0}
              familyId={familyData?.id}
              onContribute={async (amount) => {
                try {
                  const childId = currentUser.uid.replace('child:', '');
                  const childRef = doc(db, 'families', familyData.id, 'children', childId);
                  const childDoc = await getDoc(childRef);
                  if (childDoc.exists()) {
                    const currentAllowance = childDoc.data().allowance || 0;
                    await updateDoc(childRef, {
                      allowance: Math.max(0, currentAllowance - amount)
                    });
                    console.log(`💰 Deducted $${amount} from allowance for goal contribution`);
                    // Reload to reflect updated balance
                    window.location.reload();
                  }
                } catch (err) {
                  console.error('Error deducting from allowance:', err);
                }
              }}
            />
          </Grid>

          {/* Rewards Store */}
          <Grid item xs={12}>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" fontWeight="bold">
                    🏆 Rewards Store
                  </Typography>
                  <Button
                    variant={showRewards ? "contained" : "outlined"}
                    onClick={() => setShowRewards(!showRewards)}
                  >
                    {showRewards ? 'Hide' : 'Browse'} Rewards
                  </Button>
                </Box>
                {showRewards && (
                  <RewardsStore currentUser={currentUser} childProfile={userProfile} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* My Completed Tasks */}
          <Grid item xs={12}>
            <CompletedTasksCard 
              tasks={myTasks} 
              familyMembers={[userProfile]}
              isParent={false}
            />
          </Grid>
        </Grid>

        {/* Floating Action Button */}
        <Fab
          color="secondary"
          aria-label="add shopping item"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'linear-gradient(45deg, #FF9800, #FFB74D)',
            '&:hover': {
              background: 'linear-gradient(45deg, #F57C00, #FF9800)',
            }
          }}
          onClick={() => setOpenShoppingDialog(true)}
        >
          <Add />
        </Fab>

        {/* Add Shopping Item Dialog */}
        <Dialog open={openShoppingDialog} onClose={() => setOpenShoppingDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Request Shopping Item</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Item Name"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                fullWidth
                placeholder="e.g., Chocolate ice cream"
              />
              <TextField
                label="Estimated Price"
                type="number"
                value={newItem.price}
                onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
                fullWidth
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
              />
              <TextField
                label="Category (optional)"
                value={newItem.category}
                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                fullWidth
                placeholder="e.g., Snacks, Toys, Books"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenShoppingDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddShoppingItem} 
              variant="contained"
              disabled={!newItem.name || newItem.price <= 0}
            >
              Request Item
            </Button>
          </DialogActions>
        </Dialog>

        {/* Profile Customization Dialog */}
        <ProfileCustomization
          open={openProfileDialog}
          onClose={() => setOpenProfileDialog(false)}
          userProfile={userProfile}
          currentUser={currentUser}
        />

        {/* Allowance Details Dialog */}
        <Dialog open={openAllowanceDialog} onClose={() => setOpenAllowanceDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <AttachMoney color="primary" />
              My Money Tracker 💰
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h3" color="primary.main" fontWeight="bold">
                ${currentAllowance.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Current Allowance
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <Paper elevation={2} sx={{ p: 2, bgcolor: 'success.light' }}>
                    <Typography variant="h5" color="success.dark">
                      ${totalEarned.toFixed(2)}
                    </Typography>
                    <Typography variant="caption">
                      Total Earned 🏆
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper elevation={2} sx={{ p: 2, bgcolor: 'info.light' }}>
                    <Typography variant="h5" color="info.dark">
                      ${allowanceGoal}
                    </Typography>
                    <Typography variant="caption">
                      Your Goal 🎯
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  💡 Keep completing tasks to earn more!
                </Typography>
                <Typography variant="caption">
                  You're {progressPercentage.toFixed(0)}% of the way to your goal!
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAllowanceDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* My Tasks Dialog */}
        <Dialog open={openTasksDialog} onClose={() => setOpenTasksDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <Assignment color="warning" />
              My Tasks to Do ({pendingTasks.length})
            </Box>
          </DialogTitle>
          <DialogContent>
            {pendingTasks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  🎉 No tasks right now!
                </Typography>
                <Typography variant="body2">
                  Check back later for new tasks
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ pt: 1 }}>
                {pendingTasks.map((task) => (
                  <Grid item xs={12} key={task.id}>
                    <TaskCard task={task} isParent={false} />
                  </Grid>
                ))}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenTasksDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Completed Tasks Dialog */}
        <Dialog open={openCompletedDialog} onClose={() => setOpenCompletedDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircle color="success" />
              My Completed Tasks ({completedTasks.length}) 🌟
            </Box>
          </DialogTitle>
          <DialogContent>
            {completedTasks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No completed tasks yet
                </Typography>
                <Typography variant="body2">
                  Complete some tasks to see them here!
                </Typography>
              </Box>
            ) : (
              <List>
                {completedTasks.map((task) => (
                  <ListItem key={task.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        <CheckCircle />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={task.title}
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            ✅ Completed on {task.completedAt ? new Date(task.completedAt).toLocaleDateString() : 'Recently'}
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            💰 Earned: ${(task.allowance || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      }
                    />
                    <Chip
                      icon={<EmojiEvents />}
                      label="Done!"
                      color="success"
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCompletedDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Feedback Widget */}
        <FeedbackWidget />

        {/* Review Prompt */}

      </Container>

      {/* Emergency Overlay - always visible during screen lock */}
      <EmergencyOverlay familyId={familyId} isVisible={isCurrentlyLocked} />
    </Box>
  );
}

export default ChildDashboard;
